import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, LogOut, Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

export interface TopBarProps {
  breadcrumb?: ReactNode;
  onOpenDrawer?: () => void;
  showHamburger?: boolean;
}

function getInitials(input: string): string {
  const parts = input
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function emailPrefix(email: string): string {
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

export function TopBar({ breadcrumb, onOpenDrawer, showHamburger }: TopBarProps) {
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const displayName = user?.display_name || (user?.email ? emailPrefix(user.email) : '');
  const initials = getInitials(displayName || user?.email || '?');
  const roleLabel = 'Approver';

  return (
    <header
      className={cn(
        'sticky top-0 z-[--z-sticky] flex h-[56px] shrink-0 items-center gap-12',
        'border-b border-neutral-200 bg-neutral-0 px-16 laptop:px-24',
      )}
    >
      {showHamburger && (
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Open navigation"
          className="flex h-[44px] w-[44px] items-center justify-center rounded-md text-neutral-700 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 laptop:hidden"
        >
          <Menu className="h-20 w-20" aria-hidden="true" />
        </button>
      )}

      {/* Breadcrumb slot — populated by AppShell from W1-C's <Breadcrumb /> */}
      <div className="flex min-w-0 flex-1 items-center">
        {breadcrumb}
      </div>

      <div className="flex shrink-0 items-center gap-8">
        {user && (
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`Account menu for ${displayName || user.email}`}
              className={cn(
                'flex items-center gap-8 rounded-full py-4 pl-2 pr-8 transition-colors',
                'hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              )}
            >
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt=""
                  className="h-32 w-32 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-32 w-32 items-center justify-center rounded-full bg-primary-500/10 text-xs font-bold text-primary-700">
                  {initials}
                </span>
              )}
              <span className="hidden text-left leading-tight tablet:block">
                <span className="block max-w-[160px] truncate text-sm font-medium text-neutral-800">
                  {displayName || user.email}
                </span>
                <span className="block text-xs text-neutral-400">{roleLabel}</span>
              </span>
              <ChevronDown
                className="hidden h-12 w-12 text-neutral-400 tablet:block"
                aria-hidden="true"
              />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-4 w-[224px] rounded-md border border-neutral-200 bg-neutral-0 py-4 shadow-md z-[--z-dropdown]"
              >
                <div className="border-b border-neutral-100 px-12 py-8">
                  <p className="truncate text-sm font-medium text-neutral-900">
                    {displayName || user.email}
                  </p>
                  <p className="truncate text-xs text-neutral-500">{user.email}</p>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    void signOut();
                  }}
                  className="flex w-full items-center gap-8 px-12 py-8 text-sm text-neutral-700 hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none"
                >
                  <LogOut className="h-16 w-16 text-neutral-500" aria-hidden="true" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
